import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Asamblea,
  Acta,
  Voto,
  ResultadoMateriaEntity,
  CrearAsambleaRequest,
  ActualizarBorradorAsambleaRequest,
  AgregarMateriaRequest,
  PublicarAsambleaRequest,
  ExtenderVotacionRequest,
  AnularAsambleaRequest,
  RegistrarVotoRequest,
  FirmarActaRequest,
  SubirAdjuntoRequest,
  SubirAdjuntoResponse,
  DescargarActaResponse,
} from '@condo/contracts';
import { ApiRoutes } from '@condo/contracts';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/query-keys';

// ─────────────────────────────────────────────────────────────────────────────
// Response shapes
// ─────────────────────────────────────────────────────────────────────────────

interface AsambleasListResponse {
  items: Asamblea[];
}

interface ActasHistorialResponse {
  items: Array<{ asamblea: Asamblea; acta: Acta }>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Queries
// ─────────────────────────────────────────────────────────────────────────────

export function useAsambleas(condoId: string, estado?: string) {
  return useQuery({
    queryKey: [...queryKeys.asambleas(condoId), { estado }],
    queryFn: () =>
      api.get<AsambleasListResponse>(ApiRoutes.asambleas(condoId), {
        query: estado ? { estado } : undefined,
      }),
    enabled: !!condoId,
  });
}

export function useAsamblea(condoId: string, id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.asamblea(condoId, id ?? ''),
    queryFn: () => api.get<Asamblea>(ApiRoutes.asambleaById(condoId, id!)),
    enabled: !!condoId && !!id,
    refetchInterval: 30_000,
  });
}

export function useMisVotos(condoId: string, asambleaId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.asambleaMisVotos(condoId, asambleaId ?? ''),
    queryFn: () =>
      api.get<{ items: Voto[] }>(ApiRoutes.asambleaMisVotos(condoId, asambleaId!)),
    enabled: !!condoId && !!asambleaId,
  });
}

export function useResultados(condoId: string, asambleaId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.asambleaResultados(condoId, asambleaId ?? ''),
    queryFn: () =>
      api.get<{ items: ResultadoMateriaEntity[] }>(
        ApiRoutes.asambleaResultados(condoId, asambleaId!),
      ),
    enabled: !!condoId && !!asambleaId,
  });
}

export function useActa(condoId: string, asambleaId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.asambleaActa(condoId, asambleaId ?? ''),
    queryFn: () => api.get<Acta | null>(ApiRoutes.asambleaActa(condoId, asambleaId!)),
    enabled: !!condoId && !!asambleaId,
  });
}

export function useActasHistorial(condoId: string) {
  return useQuery({
    queryKey: queryKeys.actasHistorial(condoId),
    queryFn: () => api.get<ActasHistorialResponse>(ApiRoutes.actasHistorial(condoId)),
    enabled: !!condoId,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Mutations — asamblea lifecycle
// ─────────────────────────────────────────────────────────────────────────────

export function useCrearAsamblea(condoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CrearAsambleaRequest) =>
      api.post<Asamblea, CrearAsambleaRequest>(ApiRoutes.asambleas(condoId), input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.asambleas(condoId) });
    },
  });
}

export function useActualizarBorrador(condoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      asambleaId,
      ...body
    }: ActualizarBorradorAsambleaRequest & { asambleaId: string }) =>
      api.patch<Asamblea, ActualizarBorradorAsambleaRequest>(
        ApiRoutes.asambleaById(condoId, asambleaId),
        body,
      ),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: queryKeys.asambleas(condoId) });
      void qc.invalidateQueries({
        queryKey: queryKeys.asamblea(condoId, variables.asambleaId),
      });
    },
  });
}

export function useAgregarMateria(condoId: string, asambleaId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AgregarMateriaRequest) =>
      api.post<Asamblea, AgregarMateriaRequest>(
        ApiRoutes.asambleaMaterias(condoId, asambleaId),
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.asamblea(condoId, asambleaId),
      });
    },
  });
}

export function useQuitarMateria(condoId: string, asambleaId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (materiaId: string) =>
      api.delete(ApiRoutes.asambleaMateriaById(condoId, asambleaId, materiaId)),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.asamblea(condoId, asambleaId),
      });
    },
  });
}

export function usePublicarAsamblea(condoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      asambleaId,
      ...body
    }: PublicarAsambleaRequest & { asambleaId: string }) =>
      api.post<Asamblea, PublicarAsambleaRequest>(
        ApiRoutes.asambleaPublicar(condoId, asambleaId),
        body,
      ),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: queryKeys.asambleas(condoId) });
      void qc.invalidateQueries({
        queryKey: queryKeys.asamblea(condoId, variables.asambleaId),
      });
    },
  });
}

export function useCerrarAsamblea(condoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (asambleaId: string) =>
      api.post<Asamblea>(ApiRoutes.asambleaCerrar(condoId, asambleaId)),
    onSuccess: (_data, asambleaId) => {
      void qc.invalidateQueries({ queryKey: queryKeys.asambleas(condoId) });
      void qc.invalidateQueries({ queryKey: queryKeys.asamblea(condoId, asambleaId) });
    },
  });
}

export function useExtenderVotacion(condoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      asambleaId,
      ...body
    }: ExtenderVotacionRequest & { asambleaId: string }) =>
      api.post<Asamblea, ExtenderVotacionRequest>(
        ApiRoutes.asambleaExtenderVotacion(condoId, asambleaId),
        body,
      ),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: queryKeys.asamblea(condoId, variables.asambleaId),
      });
    },
  });
}

export function useAnularAsamblea(condoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      asambleaId,
      ...body
    }: AnularAsambleaRequest & { asambleaId: string }) =>
      api.post<Asamblea, AnularAsambleaRequest>(
        ApiRoutes.asambleaAnular(condoId, asambleaId),
        body,
      ),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: queryKeys.asambleas(condoId) });
      void qc.invalidateQueries({
        queryKey: queryKeys.asamblea(condoId, variables.asambleaId),
      });
    },
  });
}

export function useDeclararSegundaCitacion(condoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (asambleaId: string) =>
      api.post<Asamblea>(ApiRoutes.asambleaSegundaCitacion(condoId, asambleaId)),
    onSuccess: (_data, asambleaId) => {
      void qc.invalidateQueries({ queryKey: queryKeys.asambleas(condoId) });
      void qc.invalidateQueries({ queryKey: queryKeys.asamblea(condoId, asambleaId) });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Mutations — votación
// ─────────────────────────────────────────────────────────────────────────────

export function useVotar(condoId: string, asambleaId: string, materiaId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RegistrarVotoRequest) =>
      api.post<Voto, RegistrarVotoRequest>(
        ApiRoutes.asambleaVotar(condoId, asambleaId, materiaId),
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.asambleaMisVotos(condoId, asambleaId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.asambleaResultados(condoId, asambleaId),
      });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Mutations — acta
// ─────────────────────────────────────────────────────────────────────────────

export function useActualizarActa(condoId: string, asambleaId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (textoLibre: string) =>
      api.patch<Acta, { textoLibre: string }>(ApiRoutes.asambleaActa(condoId, asambleaId), {
        textoLibre,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.asambleaActa(condoId, asambleaId),
      });
    },
  });
}

export function useSubirAdjuntoActa(condoId: string, asambleaId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SubirAdjuntoRequest) =>
      api.post<SubirAdjuntoResponse, SubirAdjuntoRequest>(
        ApiRoutes.asambleaActaAdjuntos(condoId, asambleaId),
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.asambleaActa(condoId, asambleaId),
      });
    },
  });
}

export function useFirmarActa(condoId: string, asambleaId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FirmarActaRequest) =>
      api.post<Acta, FirmarActaRequest>(
        ApiRoutes.asambleaActaFirmar(condoId, asambleaId),
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.asambleaActa(condoId, asambleaId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.asamblea(condoId, asambleaId),
      });
    },
  });
}

export function useDescargarActa(condoId: string, asambleaId: string) {
  return useMutation({
    mutationFn: () =>
      api.get<DescargarActaResponse>(ApiRoutes.asambleaActaDescargar(condoId, asambleaId)),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Legacy — kept to avoid breaking acta.tsx / acta-preview.tsx during transition
// ─────────────────────────────────────────────────────────────────────────────

/** @deprecated Use useActa + useFirmarActa for the new workflow. */
export function useGenerarActa(condoId: string, asambleaId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: unknown) =>
      api.post<Acta>(ApiRoutes.asambleaActa(condoId, asambleaId), input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.asambleas(condoId) });
      void qc.invalidateQueries({
        queryKey: queryKeys.asamblea(condoId, asambleaId),
      });
      void qc.invalidateQueries({
        queryKey: queryKeys.asambleaActa(condoId, asambleaId),
      });
    },
  });
}
